/* eslint-disable */
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";

const PRIMARY = "#0284c7"; // Calm Sky Blue
const ACCENT = "#4f46e5";  // Deep Indigo
const BG_COLOR = "#f1f5f9"; // Soft misty slate background
const TEXT_COLOR = "#1e293b"; // Slate 800 for readability

// SVG Icons
const IconPlay = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>;
const IconPause = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>;
const IconNext = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>;
const IconPrev = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
const IconCheck = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>;
const IconX = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconCar = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>;
const IconClock = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;

function MobileCarousel({ images }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!images || images.length <= 1) return;
    const t = setInterval(() => setIdx(p => (p + 1) % images.length), 3500);
    return () => clearInterval(t);
  }, [images]);
  
  if (!images?.length) return null;
  return (
    <div className="mobile-carousel" style={{ position: "absolute", inset: "0 0 0 0", overflow: "hidden", borderTop: "1px solid rgba(255,255,255,0.3)" }}>
      {images.map((img, i) => (
        <img key={i} src={img.url || img.thumb || img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transform: i === idx ? 'scale(1)' : 'scale(1.1)', opacity: i === idx ? 1 : 0, transition: 'all 1.5s ease-out' }} />
      ))}
    </div>
  );
}

export default function SharedItinerary() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);

  const [slideIdx, setSlideIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    api.getSharedItinerary(token)
      .then(setData)
      .catch(e => setErr(e?.response?.status === 410 ? "exp" : "miss"))
      .finally(() => setBusy(false));
  }, [token]);

  const itin = data?.itinerary_data || {};
  const days = itin.days || [];
  
  const allImages = days.flatMap(d => (d.images||[])).filter(Boolean);
  const slides = [
    { type: 'cover' },
    ...days.map(d => ({ type: 'day', data: d }))
  ];
  if (allImages.length > 0) slides.push({ type: 'gallery', images: allImages });
  slides.push({ type: 'summary' });

  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      setSlideIdx(prev => {
        if (prev < slides.length - 1) return prev + 1;
        setIsPlaying(false);
        return prev;
      });
    }, 12000); 
    return () => clearInterval(timer);
  }, [isPlaying, slides.length]);

  if (busy) return <Loader />;
  if (err === "exp") return <StatusScreen msg="Link Expired" />;
  if (err || !data) return <StatusScreen msg="Link Not Found" />;

  let bgImg = "";
  if (slides[slideIdx].type === 'cover') {
    bgImg = days.flatMap(d => (d.images||[]).map(im=>im.url||im.thumb)).filter(Boolean)[0] || "";
  } else if (slides[slideIdx].type === 'day') {
    bgImg = (slides[slideIdx].data.images||[])[0]?.url || (slides[slideIdx].data.images||[])[0]?.thumb || days.flatMap(d => (d.images||[]).map(im=>im.url||im.thumb)).filter(Boolean)[0] || "";
  } else {
    bgImg = days.flatMap(d => (d.images||[]).map(im=>im.url||im.thumb)).filter(Boolean)[0] || "";
  }

  const nextSlide = () => { setSlideIdx(p => Math.min(slides.length - 1, p + 1)); setIsPlaying(false); };
  const prevSlide = () => { setSlideIdx(p => Math.max(0, p - 1)); setIsPlaying(false); };
  const togglePlay = () => setIsPlaying(!isPlaying);

  return (
    <div style={{ height: "100vh", width: "100vw", overflow: "hidden", position: "relative", backgroundColor: BG_COLOR, color: TEXT_COLOR, fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,900&family=Inter:wght@300;400;500;600;700&family=EB+Garamond:ital,wght@1,400;1,500&display=swap');
        *{box-sizing:border-box;}
        
        .ambient-bg {
          position: absolute;
          inset: -10%;
          background-size: cover;
          background-position: center;
          filter: blur(50px) brightness(1.1) opacity(0.5); /* Light misty blur */
          transition: background-image 1s ease-in-out;
          transform: scale(1.1);
          animation: ambientPan 30s ease-in-out infinite alternate;
        }
        @keyframes ambientPan { 0% { transform: scale(1.1) translate(0,0); } 100% { transform: scale(1.2) translate(-2%, -2%); } }

        .glass-container {
          position: absolute;
          inset: 30px;
          bottom: 90px;
          background: rgba(255, 255, 255, 0.45); /* Light frosted glass */
          backdrop-filter: blur(40px);
          -webkit-backdrop-filter: blur(40px);
          border: 1px solid rgba(255, 255, 255, 0.9);
          border-radius: 40px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,1);
          overflow: hidden;
          display: flex;
        }
        
        .control-bar {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          gap: 20px;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(20px);
          padding: 10px 24px;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 1);
          box-shadow: 0 10px 30px rgba(0,0,0,0.08);
          z-index: 10;
        }
        .ctrl-btn {
          background: none; border: none; color: ${TEXT_COLOR}; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          width: 44px; height: 44px; border-radius: 50%; transition: all 0.2s;
        }
        .ctrl-btn:hover { background: rgba(2, 132, 199, 0.1); transform: scale(1.1); color: ${PRIMARY}; }
        .ctrl-btn:active { transform: scale(0.95); }

        .progress-dots { display: flex; gap: 8px; }
        .dot { width: 8px; height: 8px; border-radius: 50%; background: rgba(0,0,0,0.15); transition: all 0.3s; cursor:pointer; }
        .dot.active { background: ${PRIMARY}; transform: scale(1.3); box-shadow: 0 0 10px rgba(2,132,199,0.3); }

        .slide-enter { animation: slideIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) both; }
        @keyframes slideIn { from { opacity: 0; transform: translateY(20px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }

        .scrollable { overflow-y: auto; overflow-x: hidden; }
        .scrollable::-webkit-scrollbar { width: 4px; }
        .scrollable::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 4px; }
        .scrollable::-webkit-scrollbar-thumb:hover { background: ${PRIMARY}; }

        @media (max-width: 900px) {
          .glass-container { inset: 0; bottom: 80px; border-radius: 0; border: none; box-shadow: none; flex-direction: column; background: rgba(255, 255, 255, 0.6); backdrop-filter: blur(20px); overflow-y: auto; }
          .control-bar { width: 90%; justify-content: space-between; padding: 12px 20px; bottom: 16px; }
          .progress-dots { display: none; }
          .mobile-stack { flex-direction: column !important; }
          .mobile-hide { display: none !important; }
          .left-content { padding: 24px !important; flex: none !important; height: auto !important; overflow-y: visible !important; }
          .right-content { padding: 24px !important; flex: none !important; height: 300px !important; border-left: none !important; border-top: 1px solid rgba(255,255,255,0.8); }
          .desktop-grid { display: none !important; }
          .mobile-carousel { display: block !important; }
        }
        .desktop-grid { display: grid; }
        .mobile-carousel { display: none; }
      `}</style>

      {/* Light Background Layering */}
      <div className="ambient-bg" style={{ backgroundImage: `url(${bgImg})` }} />
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at center, rgba(255,255,255,0.2) 0%, rgba(241,245,249,0.6) 100%)" }} />

      <div className="glass-container slide-enter" key={slideIdx}>
        
        {/* COVER SLIDE */}
        {slides[slideIdx].type === 'cover' && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px", textAlign: "center", background: "linear-gradient(to bottom, rgba(255,255,255,0.4), rgba(255,255,255,0.1))" }}>
            <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(2,132,199,0.1)", border: `1px solid rgba(2,132,199,0.3)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, marginBottom: 30, color: PRIMARY, boxShadow: `0 10px 30px rgba(2,132,199,0.15)` }}>✈</div>
            <div style={{ fontSize: 13, color: PRIMARY, letterSpacing: 6, fontWeight: 800, marginBottom: 16 }}>EXCLUSIVE JOURNEY</div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(36px, 6vw, 80px)", fontWeight: 900, marginBottom: 24, lineHeight: 1.1, color: "#0f172a" }}>
              {itin.trip_title || data.destination}
            </h1>
            <p style={{ fontSize: "clamp(18px, 2vw, 22px)", color: "#475569", fontStyle: "italic", fontFamily: "'EB Garamond', serif", maxWidth: 700, marginBottom: 40, lineHeight: 1.6 }}>
              {itin.route_summary}
            </p>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", justifyContent: "center" }}>
              <div style={{ background: "rgba(255,255,255,0.6)", padding: "16px 32px", borderRadius: 16, border: "1px solid rgba(255,255,255,1)", backdropFilter: "blur(10px)", boxShadow: "0 4px 15px rgba(0,0,0,0.03)" }}>
                <div style={{ fontSize: 11, color: "#64748b", letterSpacing: 2, fontWeight: 600 }}>PREPARED FOR</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>{data.customer_name}</div>
              </div>
              <div style={{ background: "rgba(255,255,255,0.6)", padding: "16px 32px", borderRadius: 16, border: "1px solid rgba(255,255,255,1)", backdropFilter: "blur(10px)", boxShadow: "0 4px 15px rgba(0,0,0,0.03)" }}>
                <div style={{ fontSize: 11, color: "#64748b", letterSpacing: 2, fontWeight: 600 }}>DURATION</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>{data.days}D / {data.nights}N</div>
              </div>
            </div>
          </div>
        )}

        {/* DAY SLIDE */}
        {slides[slideIdx].type === 'day' && (
          <>
            {/* Left Content (Text + Hotel Images) */}
            <div className="scrollable left-content" style={{ flex: "1 1 50%", padding: "40px", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 24 }}>
                <div>
                  <div style={{ fontSize: 12, color: PRIMARY, letterSpacing: 4, fontWeight: 800, marginBottom: 4 }}>DAY {slides[slideIdx].data.day_number}</div>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 800, lineHeight: 1.2, color: "#0f172a" }}>
                    {slides[slideIdx].data.route || slides[slideIdx].data.title}
                  </h2>
                </div>
              </div>

              {(slides[slideIdx].data.distance || slides[slideIdx].data.drive_time) && (
                <div style={{ display: "flex", gap: 16, marginBottom: 24, background: "rgba(255,255,255,0.6)", padding: "10px 20px", borderRadius: 12, border: "1px solid rgba(255,255,255,1)", width: "fit-content", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                  {slides[slideIdx].data.distance && <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 500, color: "#334155" }}><span style={{ color: PRIMARY }}><IconCar /></span> {slides[slideIdx].data.distance}</div>}
                  {slides[slideIdx].data.drive_time && <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 500, color: "#334155" }}><span style={{ color: PRIMARY }}><IconClock /></span> {slides[slideIdx].data.drive_time}</div>}
                </div>
              )}

              {slides[slideIdx].data.description && (
                <p style={{ fontFamily: "'EB Garamond', serif", fontSize: "clamp(18px, 2vw, 20px)", color: "#475569", lineHeight: 1.6, marginBottom: 24, fontStyle: "italic" }}>
                  {slides[slideIdx].data.description}
                </p>
              )}

              {slides[slideIdx].data.activities?.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 11, color: PRIMARY, letterSpacing: 3, fontWeight: 800, marginBottom: 12 }}>EXPERIENCES</div>
                  {slides[slideIdx].data.activities.slice(0, 6).map((a, i) => (
                    <div key={i} style={{ display: "flex", gap: 12, marginBottom: 10, alignItems: "flex-start" }}>
                      <div style={{ color: PRIMARY, marginTop: 5, width: 6, height: 6, borderRadius: "50%", background: PRIMARY, flexShrink: 0 }} />
                      <span style={{ fontSize: "15px", color: "#334155", lineHeight: 1.5, fontWeight: 500 }}>{a}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Stay & Meals Info WITH HOTEL IMAGES */}
              <div style={{ marginTop: "auto", borderTop: "1px solid rgba(0,0,0,0.06)", paddingTop: 20, display: "flex", flexDirection: "column", gap: 16 }}>
                {slides[slideIdx].data.meals && (
                  <div style={{ fontSize: 15, color: "#334155" }}>
                    <span style={{ color: ACCENT, fontWeight: 800, marginRight: 8, letterSpacing: 1 }}>MEALS:</span> {slides[slideIdx].data.meals}
                  </div>
                )}
                
                {slides[slideIdx].data.hotel_name && (
                  <div>
                    <div style={{ fontSize: 15, color: "#334155", marginBottom: 12 }}>
                      <span style={{ color: ACCENT, fontWeight: 800, marginRight: 8, letterSpacing: 1 }}>STAY:</span> <span style={{ fontWeight: 600 }}>{slides[slideIdx].data.hotel_name}</span> {slides[slideIdx].data.hotel_category ? `(${slides[slideIdx].data.hotel_category})` : ""}
                    </div>
                    
                    {/* Hotel Images below Stay */}
                    {slides[slideIdx].data.hotel_images?.length > 0 && (
                       <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                         {slides[slideIdx].data.hotel_images.slice(0,3).map((img, i) => (
                           <div key={i} style={{ width: 100, height: 70, borderRadius: 8, overflow: "hidden", border: `1px solid rgba(255,255,255,0.8)`, boxShadow: "0 4px 10px rgba(0,0,0,0.05)", position: "relative" }}>
                             <img src={img.url} alt="Hotel" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                             {i===0 && <div style={{ position:"absolute", bottom:0, left:0, right:0, background:"rgba(255,255,255,0.8)", color:ACCENT, fontSize:9, textAlign:"center", padding:"2px", fontWeight:"bold", backdropFilter: "blur(4px)" }}>HOTEL</div>}
                           </div>
                         ))}
                       </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Content (Main Images Grid - Without hard cropping) */}
            <div className="scrollable right-content" style={{ flex: "1 1 50%", background: "rgba(255,255,255,0.3)", padding: "40px", borderLeft: "1px solid rgba(255,255,255,0.8)", backdropFilter: "blur(20px)", position: "relative" }}>
              {slides[slideIdx].data.images?.length > 0 ? (
                <>
                  <div className="desktop-grid" style={{ gridTemplateColumns: "1fr 1fr", gridAutoRows: "minmax(0, 1fr)", gap: 16, height: "100%" }}>
                    {slides[slideIdx].data.images.slice(0,5).map((img, i, arr) => {
                       const isLast = i === arr.length - 1;
                       const isOddRemaining = (arr.length - 1) % 2 !== 0;
                       const isFull = i === 0 || (isLast && isOddRemaining);
                       const colSpan = isFull ? "1 / -1" : "auto";
                       return (
                         <div key={i} style={{ gridColumn: colSpan, height: "100%", borderRadius: 20, overflow: "hidden", position: "relative", border: "2px solid rgba(255,255,255,1)", boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}>
                           <img src={img.url || img.thumb || img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                         </div>
                       );
                    })}
                  </div>
                  
                  <MobileCarousel images={slides[slideIdx].data.images} />
                </>
              ) : (
                <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: 16 }}>No destination images available</div>
              )}
            </div>
          </>
        )}

        {/* SUMMARY SLIDE */}
        {slides[slideIdx].type === 'summary' && (
          <div className="scrollable" style={{ flex: 1, padding: "24px 40px", display: "flex", flexDirection: "column", background: "linear-gradient(to right, rgba(255,255,255,0.6), rgba(255,255,255,0.2))" }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 900, color: PRIMARY, marginBottom: 24, textAlign: "center" }}>Trip Summary</h2>
            
            <div className="mobile-stack" style={{ display: "flex", gap: 20, marginBottom: 24 }}>
              {itin.inclusions?.length > 0 && (
                <div style={{ flex: 1, background: "rgba(255,255,255,0.7)", padding: 20, borderRadius: 20, border: "1px solid rgba(255,255,255,1)", boxShadow: "0 10px 30px rgba(0,0,0,0.03)" }}>
                  <h3 style={{ fontSize: 13, color: "#10b981", letterSpacing: 2, marginBottom: 12, fontWeight: 800 }}>✅ INCLUSIONS</h3>
                  {itin.inclusions.map((x,i) => (
                    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}><span style={{ color: "#10b981", fontWeight: 700 }}>+</span> <span style={{ fontSize: 14, color: "#334155", lineHeight: 1.4 }}>{x}</span></div>
                  ))}
                </div>
              )}
              {itin.exclusions?.length > 0 && (
                <div style={{ flex: 1, background: "rgba(255,255,255,0.7)", padding: 20, borderRadius: 20, border: "1px solid rgba(255,255,255,1)", boxShadow: "0 10px 30px rgba(0,0,0,0.03)" }}>
                  <h3 style={{ fontSize: 13, color: "#ef4444", letterSpacing: 2, marginBottom: 12, fontWeight: 800 }}>❌ EXCLUSIONS</h3>
                  {itin.exclusions.map((x,i) => (
                    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}><span style={{ color: "#ef4444", fontWeight: 700 }}>−</span> <span style={{ fontSize: 14, color: "#334155", lineHeight: 1.4 }}>{x}</span></div>
                  ))}
                </div>
              )}
            </div>

            {(data.packages||[]).filter(p=>p.name).length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 20 }}>
                {data.packages.filter(p=>p.name).map((p,i) => (
                  <div key={i} style={{ background: "rgba(255,255,255,0.8)", borderRadius: 16, border: `1px solid rgba(255,255,255,1)`, overflow: "hidden", boxShadow: "0 10px 30px rgba(0,0,0,0.05)" }}>
                    <div style={{ background: `linear-gradient(135deg, rgba(2,132,199,0.1), rgba(2,132,199,0.05))`, padding: "16px 20px", borderBottom: `2px solid ${PRIMARY}` }}>
                      <div style={{ fontSize: 10, color: PRIMARY, fontWeight: 800, letterSpacing: 2, marginBottom: 4 }}>{p.category || "PACKAGE"}</div>
                      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 900, color: "#0f172a" }}>{p.name.replace('Luxuary', 'Luxury')}</div>
                    </div>
                    <div style={{ padding: "16px 20px" }}>
                      <div style={{ fontSize: 24, fontWeight: 900, color: PRIMARY, marginBottom: 8 }}>₹{Number(p.price_per_person).toLocaleString('en-IN')} <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>/ person</span></div>
                      {p.notes && <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.5 }}>{p.notes}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div style={{ marginTop: "auto", paddingTop: 20, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#94a3b8", letterSpacing: 2, fontWeight: 700 }}>PREPARED BY LUCKY JESWANI</div>
            </div>
          </div>
        )}

        {/* GALLERY SLIDE */}
        {slides[slideIdx].type === 'gallery' && (
          <div className="scrollable" style={{ flex: 1, padding: "30px", display: "flex", flexDirection: "column", background: "linear-gradient(to bottom, rgba(255,255,255,0.8), rgba(255,255,255,0.4))" }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 900, color: PRIMARY, marginBottom: 24, textAlign: "center" }}>Photo Gallery</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12 }}>
              {slides[slideIdx].images.map((img, i) => (
                <div key={i} style={{ aspectRatio: "1", borderRadius: 16, overflow: "hidden", border: "2px solid rgba(255,255,255,1)", boxShadow: "0 4px 15px rgba(0,0,0,0.05)" }}>
                  <img src={img.url || img.thumb || img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              ))}
            </div>
            <div style={{ paddingBottom: 60 }} />
          </div>
        )}

      </div>

      <div className="control-bar">
        <button className="ctrl-btn" onClick={prevSlide} disabled={slideIdx === 0} style={{ opacity: slideIdx === 0 ? 0.3 : 1 }}>
          <IconPrev />
        </button>

        <button className="ctrl-btn" onClick={togglePlay} style={{ width: 52, height: 52, background: isPlaying ? "rgba(255,255,255,1)" : `linear-gradient(135deg, ${PRIMARY}, #0369a1)`, color: isPlaying ? PRIMARY : "#fff", boxShadow: isPlaying ? "0 4px 10px rgba(0,0,0,0.05)" : `0 4px 15px rgba(2,132,199,0.3)` }}>
          {isPlaying ? <IconPause /> : <IconPlay />}
        </button>

        <button className="ctrl-btn" onClick={nextSlide} disabled={slideIdx === slides.length - 1} style={{ opacity: slideIdx === slides.length - 1 ? 0.3 : 1 }}>
          <IconNext />
        </button>

        <div className="mobile-hide" style={{ width: 1, height: 24, background: "rgba(0,0,0,0.1)", margin: "0 12px" }} />

        <div className="progress-dots">
          {slides.map((_, i) => (
            <div key={i} className={`dot ${slideIdx === i ? 'active' : ''}`} onClick={() => { setSlideIdx(i); setIsPlaying(false); }} />
          ))}
        </div>
      </div>
      
    </div>
  );
}

function Loader() {
  return (
    <div style={{ height:"100vh", background:BG_COLOR, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
      <div style={{ width:60, height:60, border:`3px solid rgba(2,132,199,0.2)`, borderTopColor:PRIMARY, borderRadius:"50%", animation:"spin 1s linear infinite" }} />
    </div>
  );
}

function StatusScreen({ msg }) {
  return (
    <div style={{ height:"100vh", background:BG_COLOR, display:"flex", alignItems:"center", justifyContent:"center", color: TEXT_COLOR, fontSize: 24, fontFamily: "'Playfair Display', serif" }}>
      {msg}
    </div>
  );
}
